import type { InvoiceReportReceiptType } from 'ap-openapi';
import {
  ActiveFileType,
  TAB_KEYS,
  type TabKey,
} from '../context/PaymentDetailContext';
import type { Props as HeaderProps } from '../Detail/RightContent/Preview/Header';
import type { ProcessedBusinessDocument } from './businessDocumentProcessors';
import { normalizeBusinessDocument } from './documentNormalizers';
import { extractDocumentMetadata } from './metadataExtractors';
import type { UnifiedDocument } from './types';

export interface DocumentSelectorDependencies {
  createUnifiedPreviewProps: (
    unifiedDoc: UnifiedDocument,
    receiptType?: InvoiceReportReceiptType,
    eDocMeta?: unknown,
    scanDecision?: 'warning' | 'error' | 'ok',
    showTimestamp?: boolean
  ) => HeaderProps | undefined;
  selectAndShowFile: (
    fileId: string,
    fileType: ActiveFileType,
    pdfViewerProps: HeaderProps,
    tabKey: TabKey,
    mfFileId?: string
  ) => void;
}

/**
 * Creates a document selection function for a specific processed business document
 */
export const createDocumentSelector = (
  document: ProcessedBusinessDocument,
  dependencies: DocumentSelectorDependencies
) => {
  return () => {
    const unifiedDoc = normalizeBusinessDocument(document);
    if (!unifiedDoc) return;

    const { receiptType, eDocMeta, showTimestamp } = extractDocumentMetadata(
      document.mf_file
    );

    const pdfViewerProps = dependencies.createUnifiedPreviewProps(
      unifiedDoc,
      receiptType,
      eDocMeta,
      undefined, // No scanDecision needed for business documents
      showTimestamp
    );

    if (pdfViewerProps && document.id && document.mf_file?.id) {
      dependencies.selectAndShowFile(
        document.id,
        ActiveFileType.BUSINESS_DOCUMENT,
        pdfViewerProps,
        TAB_KEYS.TRANSACTION_DOCUMENTS,
        document.mf_file.id
      );
    }
  };
};

/**
 * Selects the first business document from a list of processed documents
 */
export const selectFirstBusinessDocument = (
  processedDocuments: ProcessedBusinessDocument[],
  dependencies: DocumentSelectorDependencies
) => {
  const firstBusinessDoc = processedDocuments?.[0];
  if (!firstBusinessDoc?.id || !firstBusinessDoc.mf_file) return;

  const unifiedDoc = normalizeBusinessDocument(firstBusinessDoc);
  if (!unifiedDoc) return;

  const { receiptType, eDocMeta, showTimestamp } = extractDocumentMetadata(
    firstBusinessDoc.mf_file
  );

  const pdfViewerProps = dependencies.createUnifiedPreviewProps(
    unifiedDoc,
    receiptType,
    eDocMeta,
    undefined, // No scanDecision needed for business documents
    showTimestamp
  );

  if (pdfViewerProps && firstBusinessDoc.mf_file?.id) {
    dependencies.selectAndShowFile(
      firstBusinessDoc.id,
      ActiveFileType.BUSINESS_DOCUMENT,
      pdfViewerProps,
      TAB_KEYS.TRANSACTION_DOCUMENTS,
      firstBusinessDoc.mf_file.id
    );
  }
};
